import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated } from 'react-spring';

/**
 * Shared page container component with background slides
 * Handles the container styling and animated background slides
 */
const PageContainer = styled('div')`
  display: flex;
  background: ${(props) => props.backgroundColor || props.theme?.colors?.darkBlue || '#1a1a2e'};
  height: ${(props) => (props.fullHeight ? '100vh' : '100%!important')};
  min-height: ${(props) => (props.fullHeight ? '100vh' : 'auto')};
  width: 100%;
  align-items: ${(props) => (props.alignItems || 'center')};
  justify-content: center;
  overflow-y: ${(props) => (props.overflowY || 'visible')};
  padding-top: ${(props) => (props.paddingTop || '0')};
  padding-bottom: ${(props) => (props.paddingBottom || '0')};
  position: ${(props) => (props.position || 'relative')};
  a {
    align-self: center;
  }
  .background-image-container {
    height: ${(props) => (props.imageHeight || '100%')};
    min-height: ${(props) => (props.imageMinHeight || 'auto')};
    width: 100%;
    position: ${(props) => (props.imagePosition || 'absolute')};
    left: 0px;
    ${(props) => (props.imagePosition === 'fixed' ? `
      bottom: 0;
      top: 0;
    ` : '')}
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    z-index: ${(props) => (props.imageZIndex || '0')};
  }
`;

const BackgroundSlidesPageContainer = ({
  alignItems,
  as = 'div',
  backgroundColor,
  children,
  fullHeight,
  gradientOverlay = 'linear-gradient(-20deg, rgba(252, 96, 118, 0.5) 0%, rgba(255, 154, 68, .5) 100%)',
  imageHeight,
  imageMinHeight,
  imagePosition,
  imageTransition,
  imageZIndex,
  overflowY,
  paddingBottom,
  paddingTop,
  position,
  ...props
}) => (
  <PageContainer
    alignItems={alignItems}
    as={as}
    backgroundColor={backgroundColor}
    fullHeight={fullHeight}
    imageHeight={imageHeight}
    imageMinHeight={imageMinHeight}
    imagePosition={imagePosition}
    imageZIndex={imageZIndex}
    overflowY={overflowY}
    paddingBottom={paddingBottom}
    paddingTop={paddingTop}
    position={position}
    {...props}
  >
    {children}
    {imageTransition.map(({ item, props: transitionProps, key }) => {
      if (!item || !item.url) {
        return null;
      }
      return (
        <animated.div
          key={key}
          className='background-image-container'
          style={{
            ...transitionProps,
            backgroundImage: `${gradientOverlay}, url(${item.url})`,
          }}
        />
      );
    })}
  </PageContainer>
);

BackgroundSlidesPageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  imageTransition: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        url: PropTypes.string,
      }),
      props: PropTypes.shape({
        opacity: PropTypes.number,
        transform: PropTypes.string,
      }),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  backgroundColor: PropTypes.string,
  gradientOverlay: PropTypes.string,
  as: PropTypes.string,
  fullHeight: PropTypes.bool,
  alignItems: PropTypes.string,
  overflowY: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  position: PropTypes.string,
  imageHeight: PropTypes.string,
  imageMinHeight: PropTypes.string,
  imagePosition: PropTypes.string,
  imageZIndex: PropTypes.string,
};

BackgroundSlidesPageContainer.defaultProps = {
  alignItems: 'center',
  as: 'div',
  backgroundColor: '#1a1a2e',
  fullHeight: false,
  gradientOverlay: 'linear-gradient(-20deg, rgba(252, 96, 118, 0.5) 0%, rgba(255, 154, 68, .5) 100%)',
  imageHeight: '100%',
  imageMinHeight: 'auto',
  imagePosition: 'absolute',
  imageZIndex: '0',
  overflowY: 'visible',
  paddingBottom: '0',
  paddingTop: '0',
  position: 'relative',
};

export default BackgroundSlidesPageContainer;
